import { Button, Dialog, DialogContent, DialogTitle, Divider } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { MetricDataType } from "@entrilia/shared/api/portfolioMonitoringTypes";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../api/adminApi";
import type { Metric } from "../../../../../api/types/portfolioMonitoringTypes";
import { useMetricsPageContext } from "../MetricsPageContext";
import MetricEditor from "./MetricEditor";
import type { MetricEditorFormState } from "./metricEditorHelper";
import { getInitialMetricEditorFormState, mapFormToCreateMetricRequest } from "./metricEditorHelper";

interface Props {
  dataType: MetricDataType;
  onClose: () => void;
  onCreated: (metric: Metric) => void;
}

const createMetric = withErrorHandling(adminApi.createMetric);

const AddMetricDialog = ({ dataType, onClose, onCreated }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { metrics } = useMetricsPageContext();

  const [saving, setSaving] = useState(false);

  const [formState, setFormState] = useState<MetricEditorFormState>(() => getInitialMetricEditorFormState(dataType));

  const handleCreateMetric = async () => {
    if (!formState.isFormValid) {
      return;
    }

    setSaving(true);
    const [newMetric, error] = await createMetric(mapFormToCreateMetricRequest(formState));
    setSaving(false);

    if (error) {
      sendNotificationError(error.message);
      logError(error, "[CreateMetricDialog] createMetric");
    } else {
      sendNotification("Metric added successfully");
      onCreated(newMetric);
    }
  };

  const otherMetricNames = metrics.map((m) => m.name);
  const allMetricCategories = distinct(metrics.map((m) => m.category).filter(Boolean)) as string[];

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Metric</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />

      <DialogContent>
        <MetricEditor
          form={formState}
          onChange={setFormState}
          otherMetricNames={otherMetricNames}
          allMetricCategories={allMetricCategories}
        />
      </DialogContent>

      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button disabled={!formState.isFormValid} variant="contained" loading={saving} onClick={handleCreateMetric}>
          Add
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default AddMetricDialog;
